import { useTranslation } from 'react-i18next';
import { t as translate } from 'i18n';
import React, { useEffect, useId, useState } from 'react';
import {
  Alert, Box, Button, Dialog, DialogActions, DialogContent,
  DialogTitle, Stack, Typography,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import { preparePassword, validatePassword } from 'api/account';
import { extractApiError } from 'api/errors';
import AccountField from './AccountField';

const ruleLabel = (rule) => ({
  length: translate('shell.passwordLength', { count: rule.min_length }),
  similarity: translate('shell.passwordSimilarity'),
  common: translate('shell.passwordCommon'),
  numeric: translate('shell.passwordNumeric'),
}[rule.kind] || rule.help_text);
const EMPTY_POLICY = [];

export default function PasswordChangeDialog({ policy = EMPTY_POLICY, mfaEnabled, onSubmit, onClose }) {
  const { t: translate } = useTranslation();
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [code, setCode] = useState('');
  const [step, setStep] = useState('password');
  const [needsMfa, setNeedsMfa] = useState(mfaEnabled);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [codeError, setCodeError] = useState('');
  const [retry, setRetry] = useState(0);
  const [validation, setValidation] = useState({ source: '', pending: false, valid: false, rules: policy, error: '' });
  const rulesId = useId();

  useEffect(() => {
    if (!newPassword) {
      setValidation({ source: '', pending: false, valid: false, rules: policy, error: '' });
      return undefined;
    }
    const controller = new AbortController();
    let current = true;
    setValidation({ source: newPassword, pending: true, valid: false, rules: policy, error: '' });
    const timer = setTimeout(async () => {
      try {
        const result = await validatePassword(newPassword, controller.signal);
        if (current) setValidation({ ...result, source: newPassword, pending: false, error: '' });
      } catch (err) {
        if (current && !controller.signal.aborted) setValidation({ source: newPassword, pending: false, valid: false, rules: policy, error: translate('shell.passwordValidationFailed') });
      }
    }, 350);
    return () => { current = false; clearTimeout(timer); controller.abort(); };
  }, [newPassword, policy, retry, translate]);

  const matches = Boolean(newPassword && confirmation && newPassword === confirmation);
  const mismatch = Boolean(confirmation && newPassword !== confirmation);
  const checked = validation.source === newPassword && !validation.pending && !validation.error;
  const ready = Boolean(password && matches && checked && validation.valid);
  const rules = validation.source === newPassword ? validation.rules : policy;
  const showRules = Boolean(newPassword && checked);

  const submissionError = (err) => {
    const data = err.response?.data;
    if (data?.current_password) {
      setStep('password');
      setPasswordError(translate('shell.currentPasswordIncorrect'));
    } else if (data?.new_password) {
      setStep('password'); setRetry((value) => value + 1);
      setError(translate('shell.passwordInvalid'));
    } else if (data?.code === 'invalid_code') {
      setNeedsMfa(true); setStep('code');
      setCodeError(translate('shell.passwordCodeInvalid'));
    } else setError(extractApiError(err, translate('shell.passwordChangeFailed')));
  };

  const submit = async (event) => {
    event.preventDefault();
    if (busy || (step === 'password' && !ready) || (step === 'code' && !code.trim())) return;
    setBusy(true); setError(''); setPasswordError(''); setCodeError('');
    try {
      if (step === 'password') {
        const result = await preparePassword({ current_password: password, new_password: newPassword });
        setValidation({ ...result, source: newPassword, pending: false, error: '' });
        if (!result.valid) return;
        setNeedsMfa(result.mfa_required);
        if (result.mfa_required) { setStep('code'); return; }
      }
      await onSubmit({ current_password: password, new_password: newPassword, ...(step === 'code' ? { code } : {}) });
      onClose();
    } catch (err) { submissionError(err); }
    finally { setBusy(false); setCode(''); }
  };

  return (
    <Dialog open onClose={() => { if (!busy) onClose(); }} maxWidth="xs" fullWidth>
      <Box component="form" onSubmit={submit} sx={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        <DialogTitle>{translate('shell.changePassword')}{needsMfa && <Typography component="p" variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>{step === 'code' ? translate('shell.passwordStepCode') : translate('shell.passwordStepNew')}</Typography>}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2.5} sx={{ pt: 1 }}>
            {error && <Alert severity="error">{error}</Alert>}
            {step === 'password' ? <>
              <AccountField label={translate('shell.currentPassword')} type="password" autoComplete="current-password" value={password} onChange={(event) => { setPassword(event.target.value); setPasswordError(''); }} error={Boolean(passwordError)} helperText={passwordError} disabled={busy} required autoFocus inputProps={{ maxLength: 4096 }} />
              <AccountField label={translate('shell.newPassword')} type="password" autoComplete="new-password" value={newPassword} onChange={(event) => { setNewPassword(event.target.value); setError(''); }} error={Boolean(newPassword && showRules && !validation.valid)} disabled={busy} required inputProps={{ maxLength: 4096, 'aria-describedby': rulesId }} />
              <Box id={rulesId}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>{translate('shell.passwordRequirements')}</Typography>
                <Stack component="ul" spacing={0.75} sx={{ listStyle: 'none', p: 0, m: 0 }} aria-live="polite">
                  {rules.map((rule) => {
                    const passed = newPassword && rule.kind === 'length'
                      ? [...newPassword].length >= rule.min_length
                      : showRules ? rule.passed : undefined;
                    const Icon = passed === true ? CheckCircleOutlineIcon : passed === false ? HighlightOffIcon : RadioButtonUncheckedIcon;
                    return <Stack component="li" direction="row" spacing={1} key={rule.id} sx={{ alignItems: 'flex-start', color: passed === true ? 'success.main' : passed === false ? 'error.main' : 'text.secondary' }}>
                      <Icon sx={{ fontSize: 18, mt: '2px !important' }} titleAccess={passed === true ? translate('shell.passed') : passed === false ? translate('shell.notPassed') : translate('shell.notChecked')} />
                      <Typography variant="body2">{ruleLabel(rule)}</Typography>
                    </Stack>;
                  })}
                </Stack>
                {validation.error && <Alert severity="error" sx={{ mt: 1 }} action={<Button onClick={() => setRetry((value) => value + 1)}>{translate('shell.retry')}</Button>}>{validation.error}</Alert>}
              </Box>
              <AccountField label={translate('shell.repeatNewPassword')} type="password" autoComplete="new-password" value={confirmation} onChange={(event) => setConfirmation(event.target.value)} error={mismatch} helperText={mismatch ? translate('shell.passwordMismatch') : matches ? translate('shell.passwordMatch') : ''} FormHelperTextProps={{ sx: matches ? { color: 'success.main' } : {} }} disabled={busy} required inputProps={{ maxLength: 4096 }} />
              <Typography variant="body2" color="text.secondary">{translate('shell.passwordSignOutOthers')}</Typography>
            </> : <>
              <Typography variant="body2" color="text.secondary">{translate('shell.passwordConfirmHint')}</Typography>
              <AccountField key="confirmation-code" label={translate('shell.authRecoveryCode')} autoComplete="one-time-code" value={code} onChange={(event) => { setCode(event.target.value); setCodeError(''); }} error={Boolean(codeError)} helperText={codeError} disabled={busy} required autoFocus inputProps={{ maxLength: 32 }} />
            </>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={busy}>{translate('shell.cancel')}</Button>
          {step === 'code' && <Button onClick={() => { setStep('password'); setCode(''); setCodeError(''); setError(''); }} disabled={busy}>{translate('shell.back')}</Button>}
          <Button type="submit" variant="contained" disabled={busy || (step === 'password' ? !ready : !code.trim())}>
            {busy ? translate('shell.checking') : step === 'password' && needsMfa ? translate('shell.next') : translate('shell.changePassword')}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
}
